import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { PnlHistoryPointDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

interface PnlChartProps {
  history: PnlHistoryPointDto[] | undefined;
  groupBy: 'day' | 'week' | 'month';
  currency: string;
  onGroupByChange: (value: 'day' | 'week' | 'month') => void;
}

export function PnlChart({ currency, groupBy, history, onGroupByChange }: PnlChartProps) {
  const data = (history ?? []).map((point) => ({ date: point.date, pnl: Number(point.pnl) }));

  return (
    <div className="rounded border border-border bg-surface p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">P&L History</h2>
        <div className="inline-flex rounded border border-border" aria-label="P&L grouping">
          {(['day', 'week', 'month'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={groupBy === value}
              onClick={() => onGroupByChange(value)}
              className="px-3 py-2 text-sm capitalize text-text-muted aria-pressed:bg-surface-raised aria-pressed:text-text"
            >
              {value}
            </button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">No realised P&L for this range.</p>
      ) : (
        <div
          className="mt-4 h-72"
          role="img"
          aria-label={`P&L history bar chart grouped by ${groupBy} with ${data.length} periods.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid stroke="#2A3142" vertical={false} />
              <XAxis dataKey="date" stroke="#A1A8B8" tick={{ fontSize: 12 }} />
              <YAxis
                stroke="#A1A8B8"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(Number(value), currency)}
                width={88}
              />
              <Tooltip
                contentStyle={{
                  background: '#222837',
                  border: '1px solid #2A3142',
                  color: '#E6E9EF',
                }}
                formatter={(value) => formatCurrency(Number(value), currency)}
              />
              <Bar dataKey="pnl" name="P&L" isAnimationActive={false}>
                {data.map((point) => (
                  <Cell key={point.date} fill={point.pnl >= 0 ? '#10B981' : '#EF4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
